/*
Licensed Materials - Property of IBM
694906H
(c) Copyright IBM Corp.  2020 All Rights Reserved

US Government Users Restricted Rights - Use, duplication or disclosure restricted
by GSA ADP Schedule Contract with IBM Corp.
*/

import React from 'react';
import * as S from './DropdownLanguage.styles';
import englishFlag from '../../../assets/flags/en-US.png';
import { useRef } from 'react';
import { gql } from '@apollo/client';
// eslint-disable-next-line monorepo-cop/no-relative-import-outside-package
import { client } from '../../../../../../../apps/newcleo/applications';
import { getBrowserLanguage, getSpaceDetails } from '@exo/frontend-common-utils';

interface langType {
  id: string;
  label: string;
  image: string;
  displayLabel: string;
}

const defaultLanguages = [
  {
    id: 'en-US',
    label: 'English',
    image: englishFlag,
    displayLabel: 'English'
  }
] as langType[];

const setLocalStorageLang = lang => {
  localStorage.setItem('appLanguage', JSON.stringify(lang));
};

const preparePredefinedLanguage = (languagesProp: langType[] = []): langType => {
  let languages = defaultLanguages;
  let predefinedLanguage = languages[0];
  if (languagesProp && languagesProp.length > 0) {
    languages = languagesProp;
  }

  const lang = localStorage.getItem('appLanguage');
  if (!lang) {
    predefinedLanguage = getBrowserLanguage(languages);
    setLocalStorageLang(predefinedLanguage);
    return predefinedLanguage;
  }

  try {
    const localLang = JSON.parse(lang);
    const find = languages.find(item => item.id === localLang.id);
    if (find) {
      setLocalStorageLang(find);
      return find;
    }
  } catch (error) {
    predefinedLanguage = getBrowserLanguage(languages);
    setLocalStorageLang(predefinedLanguage);
    return predefinedLanguage;
  }

  return predefinedLanguage;
};

const getRouteSlug = async locale => {
  const GET_ROUTE = gql`
    query GetRoute($locale: String!, $name: String!) {
      landingPageCollection(locale: $locale, where: { name: $name }) {
        items {
          name
          slug
        }
      }
      commonInnerPageCollection(locale: $locale, where: { name: $name }) {
        items {
          name
          slug
        }
      }
      newsArticleCollection(locale: $locale, where: { name: $name }) {
        items {
          name
          slug
        }
      }
    }
  `;
  let currentPageName = 'Home';
  try {
    const currentPage = JSON.parse(localStorage.getItem('currentPage') || '{}');
    currentPageName = currentPage.name || '';
  } catch (err) {
    console.error('Invalid JSON in localStorage for currentPage');
  }
  const query_data = (await client.query({
    query: GET_ROUTE,
    variables: { locale: locale.id, name: currentPageName }
  })) as any;
  const loadNewCleoModuleLanding =
    query_data?.data?.landingPageCollection?.items?.map(item => ({
      name: item.name,
      slug: item.slug
    })) || [];
  const loadNewCleoModuleCommon =
    query_data?.data?.commonInnerPageCollection?.items?.map(item => ({
      name: item.name,
      slug: item.slug
    })) || [];
  const loadNewCleoArticleCollection =
    query_data?.data?.newsArticleCollection?.items?.map(item => ({
      name: item.name,
      slug: item.slug
    })) || [];
  const final = [
    ...loadNewCleoModuleLanding,
    ...loadNewCleoModuleCommon,
    ...loadNewCleoArticleCollection
  ];

  return final.find(item => item.name === currentPageName).slug;
};

const DropdownOption = ({
  label,
  image,
  classValue
}: {
  label: string;
  image: string | null;
  classValue: string;
}) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    {image && (
      <>
        <img src={image} alt={label} style={{ marginRight: '5px' }} />
      </>
    )}
    <span className={classValue}>{label}</span>
  </div>
);

export const DropdownLanguage = ({}: Props) => {
  const dropdownLanguageRef = useRef<any>(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const handleClickOutside = (event: any) => {
    if (dropdownLanguageRef.current && !dropdownLanguageRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  const [languages, setLanguages] = React.useState<langType[]>([]);
  const [selectedItem, setSelectedItem] = React.useState<langType>();

  React.useEffect(() => {
    const fetchLanguages = async () => {
      const response = await getSpaceDetails();
      setLanguages(response);
    };
    fetchLanguages();

    document.addEventListener('click', handleClickOutside, false);
    return () => {
      document.removeEventListener('click', handleClickOutside, false);
    };
  }, []);

  React.useEffect(() => {
    if (languages.length > 0) {
      const predefinedLang = preparePredefinedLanguage(languages);
      setSelectedItem(predefinedLang);
    }
  }, [languages]);

  const changeLanguage = async (lang: langType) => {
    setSelectedItem(lang);
    setLocalStorageLang(lang);
    setIsOpen(false);
    const newRoute = await getRouteSlug(lang);
    window.history.replaceState(null, '', newRoute);
    window.location.reload();
  };

  return (
    <>
      {selectedItem && (
        <>
          <S.DropdownLanguage className="dropdown-language" ref={dropdownLanguageRef}>
            <div className="dropdown-position">
              <button onClick={() => setIsOpen(!isOpen)} className="dropdown-button">
                <DropdownOption
                  label={selectedItem.displayLabel}
                  image={selectedItem.image}
                  classValue="dropdown-selected-label"
                />
              </button>
              {isOpen && (
                <div className="dropdown-menu-position">
                  {languages.map(lang => (
                    <div
                      key={lang.id}
                      onClick={() => changeLanguage(lang)}
                      className={`dropdown-item ${selectedItem.id === lang.id ? 'dropdown-selected' : ''}`}
                    >
                      <DropdownOption
                        label={lang.displayLabel}
                        image={lang.image}
                        classValue="dropdown-label"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </S.DropdownLanguage>
        </>
      )}
    </>
  );
};

type Props = {};